import { useEffect, useState } from "react";
import dayjs from "dayjs";

import {
  DatepickerComponent,
  DropZoneComponent,
  SpinnerComponent,
  TextFieldComponent,
} from "@zenith/components";
import {
  BANK_VERIFICATION_NUMBER_LENGTH,
  FILE_INVALID_TYPE_BACK_OFFICE,
  INTERNATIONAL_PASSPORT_NUMBER_LENGTH,
  INTERNATIONAL_PASSPORT_REGEX,
  MIN_SIZE_BACK_OFFICE,
  NUMBER_INPUT_REGEX,
  ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
  RESIDENT_PERMIT_NUMBER_LENGHT,
  RESIDENT_PERMIT_NUMBER_REGEX,
} from "@zenith/data/constants";
import {
  ApiResponseDTOUserDTO,
  CancelablePromise,
  UsersApiService,
} from "@zenith/services/user";
import { getMaxDateOfBirth, getMinExpiryDate } from "@zenith/utils";
import {
  ErrorMessages,
  FieldErrors,
  FieldRules,
  ForeignerForm,
  ForeignerFormKey,
} from "./foreigner-form.model";
import { base64toFile, directorAlreadyExists } from "../director-details.util";
import { Directors } from "../interfaces";

/* eslint-disable-next-line */
export interface ForeignerFormComponentProps {
  setAddDirectorDisabled?: (state: boolean) => void;
  saveForeignerForm: (form: ForeignerForm) => void;
  editFormState?: ForeignerForm;
  editingMode?: boolean;
  directors?: Directors;
}

export function ForeignerFormComponent(props: ForeignerFormComponentProps) {
  const initialFormData: ForeignerForm = {
    bvn: "",
    name: "",
    date: null,
    residentPermitNumber: "",
    residentPermitExpiryDate: null,
    residentPermitDoc: "",
    passportExpiryDate: null,
    passportNumber: "",
    passportDoc: "",
  };

  const initialFormDataErrors = Object.keys(initialFormData).reduce(
    (acc, key) => {
      Object.assign(acc, {
        [key]: {
          hasError: false,
          message: "",
        },
      });
      return acc;
    },
    {} as FieldErrors
  );

  const formErrorMessages: ErrorMessages = {
    bvn: {
      required: "BVN is required",
      length: "BVN not valid",
    },
    date: {
      required: "Date of birth is required",
      invalid: "BVN details mismatch",
    },
    residentPermitNumber: {
      required: "Resident permit number is required",
      invalid: "Resident permit number not valid",
    },
    residentPermitExpiryDate: {
      required: "Expiry date is required",
    },
    passportNumber: {
      required: "Passport number is required",
      invalid: "Passport number not valid",
    },
    passportExpiryDate: {
      required: "Expiry date is required",
    },
  };

  const formFieldChangeRules: FieldRules = {
    bvn: NUMBER_INPUT_REGEX,
    residentPermitNumber: ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
    passportNumber: ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
  };

  const formFieldBlurRules: FieldRules = {
    residentPermitNumber: RESIDENT_PERMIT_NUMBER_REGEX,
    passportNumber: INTERNATIONAL_PASSPORT_REGEX,
  };

  const [formData, setFormData] = useState<ForeignerForm>(
    props.editFormState ? props.editFormState : initialFormData
  );
  const [uploadedResidentPermitDoc] = useState<File | undefined>(
    props.editFormState
      ? base64toFile(
          props.editFormState.residentPermitDoc,
          "resident-permit-document"
        )
      : undefined
  );
  const [uploadedPassportDoc] = useState<File | undefined>(
    props.editFormState
      ? base64toFile(props.editFormState.passportDoc, "passport-bio-document")
      : undefined
  );
  const [bvnName, setBvnName] = useState<string | undefined>(undefined);
  const [formDataErrors, setFormDataErrors] = useState<FieldErrors>(
    initialFormDataErrors
  );
  const [isBVNLoading, setBVNLoading] = useState<boolean>(false);
  const [bvnCalendarOpen, setBVNCalendarOpen] = useState<boolean>(false);
  const [residentPermitCalendarOpen, setResidentPermitCalendarOpen] =
    useState<boolean>(false);
  const [passportCalendarOpen, setPassportCalendarOpen] =
    useState<boolean>(false);

  const [ongoingVerifyBvn, setOngoingVerifyBvn] = useState<
    CancelablePromise<ApiResponseDTOUserDTO> | undefined
  >(undefined);

  useEffect(() => {
    const hasError = Object.values(formDataErrors).some(
      (item) => item.hasError
    );
    const isFilled = Object.values(formData).every((item) => !!item);
    props.saveForeignerForm(formData);
    if (props.setAddDirectorDisabled) {
      props.setAddDirectorDisabled(!isFilled || hasError);
    }
  }, [formDataErrors, formData]);

  const applyRule = (rule: RegExp | undefined, value: string): string => {
    if (!rule) {
      return value;
    }
    return value.replace(rule, "");
  };

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: ForeignerFormKey
  ) => {
    event.currentTarget.value = applyRule(
      formFieldChangeRules[field],
      event.currentTarget.value
    );
    onFieldChange(event.currentTarget.value, field);
  };

  const onDateFieldChange = (event: any, field: ForeignerFormKey): void => {
    onFieldChange(event, field);
  };

  const onFieldChange = (value: string, field: ForeignerFormKey) => {
    setFormDataErrors({
      ...formDataErrors,
      [field]: {
        hasError: false,
        message: "",
      },
    });
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const onInputBlur = (
    event: React.FocusEvent<HTMLInputElement, Element>,
    field: ForeignerFormKey
  ) => {
    const value = event.currentTarget.value;
    const fieldLength = event.currentTarget.getAttribute("maxlength");
    onFieldBlur(value, field, fieldLength);
  };

  const onFieldBlur = (
    value: string,
    field: ForeignerFormKey,
    maxLength?: string | null
  ) => {
    const requiredMessage = formErrorMessages[field]?.required;

    if (value === "" && requiredMessage) {
      setFormDataErrors({
        ...formDataErrors,
        [field]: {
          hasError: true,
          message: requiredMessage,
        },
      });
      return;
    }
    const lengthMessage = formErrorMessages[field]?.length;

    if (maxLength && value.length < Number(maxLength) && lengthMessage) {
      setFormDataErrors({
        ...formDataErrors,
        [field]: {
          hasError: true,
          message: lengthMessage,
        },
      });
      return;
    }
    const invalidMessage = formErrorMessages[field]?.invalid;
    if (
      formFieldBlurRules[field] &&
      !formFieldBlurRules[field]?.test(value) &&
      invalidMessage
    ) {
      setFormDataErrors({
        ...formDataErrors,
        [field]: {
          hasError: true,
          message: invalidMessage,
        },
      });
      return;
    }
  };

  // Combine first and last name to create director name
  const getDirectorName = (firstName: string, lastName: string): string => {
    const fullName = [firstName, lastName].filter(Boolean).join(" ");
    return fullName.trim();
  };

  useEffect(() => {
    if (bvnName !== undefined) {
      setFormData({
        ...formData,
        name: bvnName,
      });
    }
  }, [bvnName]);

  useEffect(() => {
    if (ongoingVerifyBvn) {
      ongoingVerifyBvn.cancel();
      setOngoingVerifyBvn(undefined);
    }
    setBvnName("");
    setFormDataErrors({
      ...formDataErrors,
      date: {
        hasError: false,
        message: "",
      },
    });

    if (
      formData.date &&
      formData.bvn?.length === BANK_VERIFICATION_NUMBER_LENGTH &&
      !formDataErrors.bvn.hasError
    ) {
      if (props.editingMode) {
        return;
      }
      if (
        props.directors &&
        directorAlreadyExists(props.directors, formData.bvn)
      ) {
        setFormDataErrors({
          ...formDataErrors,
          bvn: {
            hasError: true,
            message: "BVN already exists in Director’s list",
          },
        });
        return;
      }
      setBVNLoading(true);
      const bankVerificationNumber = formData.bvn;
      const verifyBvnRequest = UsersApiService.verifyBvn({
        bankVerificationNumber,
      });
      verifyBvnRequest
        .then((response: ApiResponseDTOUserDTO) => {
          const directorName = getDirectorName(
            response.data?.firstName ?? "",
            response.data?.lastName ?? ""
          );
          const birthDate = response.data?.birthdate;
          const formBvnDate = formData.date?.format("YYYY-MM-DD");
          if (birthDate === formBvnDate) {
            setBvnName(directorName);
          } else {
            setFormDataErrors({
              ...formDataErrors,
              date: {
                hasError: true,
                message: formErrorMessages.date?.invalid || "",
              },
            });
          }
          setBVNLoading(false);
        })
        .catch((error) => {
          if (error.name === "CancelError") return;
          setBVNLoading(false);
          setFormDataErrors({
            ...formDataErrors,
            bvn: {
              hasError: true,
              message: formErrorMessages.bvn?.length || "",
            },
          });
          return;
        });
      setOngoingVerifyBvn(verifyBvnRequest);
    }
  }, [formDataErrors.bvn.hasError, formData.bvn, formData.date]);

  const getImage = (image: File | null, field: ForeignerFormKey) => {
    const reader = new FileReader();
    if (!image) return;
    reader.readAsDataURL(image);
    reader.onload = function () {
      setFormData({
        ...formData,
        [field]: reader.result,
      });
    };
  };

  const deleteImage = (field: ForeignerFormKey) => {
    setFormData({
      ...formData,
      [field]: "",
    });
  };

  return (
    <div>
      <div className="mb-5">
        <TextFieldComponent
          id="bvn-field"
          labelId="bvn-field-label"
          className="grow"
          containerClassName="flex grow"
          maxLength={BANK_VERIFICATION_NUMBER_LENGTH}
          disabled={!!props.editingMode}
          hasError={formDataErrors.bvn.hasError}
          label="Bank Verification Number (BVN)"
          placeholder="Enter bank verification number"
          type="tel"
          value={formData.bvn}
          onChange={(event) => onInputChange(event, "bvn")}
          onBlur={(event) => onInputBlur(event, "bvn")}
        />
        {formDataErrors.bvn.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.bvn.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-5">
        <DatepickerComponent
          onOpen={() => setBVNCalendarOpen(true)}
          onClose={() => setBVNCalendarOpen(false)}
          id="date-of-birth"
          labelId="date-of-birth-label"
          className="w-full [&>label]:text-main-200-50"
          format="DD/MM/YYYY"
          label="Date of Birth"
          maxDate={getMaxDateOfBirth()}
          formatDensity="spacious"
          isOpen={bvnCalendarOpen}
          showCalendarIcon={false}
          disableOpenPicker={false}
          disabled={!!props.editingMode}
          hasError={formDataErrors.date.hasError}
          value={formData.date}
          onChange={(event) => onDateFieldChange(event, "date")}
          validationMessage={""}
          slotProps={{
            field: {
              onBlur: () =>
                onFieldBlur(
                  formData.date?.isValid() ? formData.date.toISOString() : "",
                  "date"
                ),
            },
            textField: {
              placeholder: "Select a date",
              onKeyDown: (event) => event.preventDefault(),
              onClick: () => {
                setBVNCalendarOpen(!bvnCalendarOpen);
              },
              focused: false,
            },
          }}
        />
        {formDataErrors.date.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.date.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-8">
        <TextFieldComponent
          id="bvn-name-field"
          labelId="bvn-name-label"
          className="grow"
          containerClassName="flex grow"
          label="Director's Name"
          placeholder="Director's name"
          type="tel"
          value={formData.name}
          onChange={(event) => onInputChange(event, "name")}
          disabled={true}
          endDecorator={
            isBVNLoading ? (
              <SpinnerComponent size={25} type={"primary"} showIcon={false} />
            ) : (
              ""
            )
          }
        />
      </div>
      <div className="mb-8 mt-2">
        <h4 className="mb-1 text-lg font-bold">
          Upload Identification Document
        </h4>
        <p className="text-sm font-extralight text-neutral-400">
          Maximum file size is 10MB. Supported file types are JPEG, PNG and PDF.
          Ensure the document is still valid.
        </p>
      </div>
      <h4 className="mb-5 text-lg font-bold">Resident Permit</h4>
      <div className="mb-5 mt-2">
        <TextFieldComponent
          id="resident-permit-number-field"
          labelId="resident-permit-number-field-label"
          className="grow"
          containerClassName="flex grow"
          maxLength={RESIDENT_PERMIT_NUMBER_LENGHT}
          hasError={formDataErrors.residentPermitNumber.hasError}
          label="Resident Permit Number"
          placeholder="Enter resident permit number"
          type="tel"
          value={formData.residentPermitNumber}
          onChange={(event) => onInputChange(event, "residentPermitNumber")}
          onBlur={(event) => onInputBlur(event, "residentPermitNumber")}
        />
        {formDataErrors.residentPermitNumber.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.residentPermitNumber.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-5">
        <DatepickerComponent
          onOpen={() => setResidentPermitCalendarOpen(true)}
          onClose={() => {
            setResidentPermitCalendarOpen(false);
          }}
          id="resident-permit-date-expiry"
          labelId="resident-permit-date-expiry-label"
          className="w-full [&>label]:text-main-200-50"
          format="DD/MM/YYYY"
          label="Expiry Date"
          minDate={getMinExpiryDate()}
          formatDensity="spacious"
          isOpen={residentPermitCalendarOpen}
          showCalendarIcon={false}
          disableOpenPicker={false}
          hasError={formDataErrors.residentPermitExpiryDate.hasError}
          value={formData.residentPermitExpiryDate}
          onChange={(event) =>
            onDateFieldChange(event, "residentPermitExpiryDate")
          }
          validationMessage={""}
          slotProps={{
            field: {
              onBlur: () =>
                onFieldBlur(
                  formData.residentPermitExpiryDate?.isValid()
                    ? formData.residentPermitExpiryDate.toISOString()
                    : "",
                  "residentPermitExpiryDate"
                ),
            },
            textField: {
              placeholder: "Select a date",
              onKeyDown: (event) => event.preventDefault(),
              onClick: () => {
                setResidentPermitCalendarOpen(!residentPermitCalendarOpen);
              },
              focused: false,
            },
          }}
        />
        {formDataErrors.residentPermitExpiryDate.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.residentPermitExpiryDate.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-6 cursor-pointer">
        <DropZoneComponent
          fieldName={"Resident Permit - Front"}
          acceptedFileTypes={["jpeg", "png", "pdf"]}
          acceptedMinSize={MIN_SIZE_BACK_OFFICE}
          fileInvalidTypeErrorMessage={FILE_INVALID_TYPE_BACK_OFFICE}
          file={uploadedResidentPermitDoc}
          getImage={(image: File | null) =>
            getImage(image, "residentPermitDoc")
          }
          deleteImage={() => deleteImage("residentPermitDoc")}
        />
      </div>

      <h4 className="mb-5 text-lg font-bold">International Passport</h4>

      <div className="mb-5 mt-2">
        <TextFieldComponent
          id="passport-number-field"
          labelId="passport-number-field-label"
          className="grow"
          containerClassName="flex grow"
          maxLength={INTERNATIONAL_PASSPORT_NUMBER_LENGTH}
          hasError={formDataErrors.passportNumber.hasError}
          label="Passport Number"
          placeholder="Enter passport number"
          type="text"
          value={formData.passportNumber}
          onChange={(event) => onInputChange(event, "passportNumber")}
          onBlur={(event) => onInputBlur(event, "passportNumber")}
        />
        {formDataErrors.passportNumber.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.passportNumber.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-5">
        <DatepickerComponent
          onOpen={() => setPassportCalendarOpen(true)}
          onClose={() => {
            setPassportCalendarOpen(false);
          }}
          id="passport-date-expiry"
          labelId="passport-date-expiry-label"
          className="w-full [&>label]:text-main-200-50"
          format="DD/MM/YYYY"
          label="Expiry Date"
          minDate={getMinExpiryDate()}
          formatDensity="spacious"
          isOpen={passportCalendarOpen}
          showCalendarIcon={false}
          disableOpenPicker={false}
          hasError={formDataErrors.passportExpiryDate.hasError}
          value={formData.passportExpiryDate}
          onChange={(event) => onDateFieldChange(event, "passportExpiryDate")}
          validationMessage={""}
          slotProps={{
            field: {
              onBlur: () =>
                onFieldBlur(
                  formData.passportExpiryDate?.isValid()
                    ? formData.passportExpiryDate.toISOString()
                    : "",
                  "passportExpiryDate"
                ),
            },
            textField: {
              placeholder: "Select a date",
              onKeyDown: (event) => event.preventDefault(),
              onClick: () => {
                setPassportCalendarOpen(!passportCalendarOpen);
              },
              focused: false,
            },
          }}
        />
        {formDataErrors.passportExpiryDate.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.passportExpiryDate.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-6 cursor-pointer">
        <DropZoneComponent
          fieldName={"Passport Bio Page"}
          acceptedFileTypes={["jpeg", "png", "pdf"]}
          file={uploadedPassportDoc}
          getImage={(image: File | null) => getImage(image, "passportDoc")}
          deleteImage={() => deleteImage("passportDoc")}
        />
      </div>
    </div>
  );
}

export default ForeignerFormComponent;
